/**
 * Helpers for uploading a "proof photo" attached to a ResponsibilityClaim.
 *
 * Mirrors the iOS proof-photo flow in
 * Views/Responsibilities/ResponsibilitiesView.swift (lines 399-406, 558-580):
 * the user picks an image when they tap "Start It" or "Mark as Done", we
 * compress it on-device, upload it to Firebase Storage, and store the
 * resulting download URL on the claim doc as `photoUrl`.
 *
 * The Firestore field write (and any other claim-status update) is intentionally
 * NOT done here so this helper composes cleanly with whatever else the caller
 * is doing to the claim document. The caller is expected to:
 *   1. await uploadResponsibilityClaimPhoto(...) -> returns string URL
 *   2. await updateDoc(claimRef, { photoUrl, ...otherFields })
 *
 * Storage path matches the existing `storage.rules` allow rule:
 *   responsibilities/{householdId}/{claimId}/{timestamp}.jpg
 */

import { ref, uploadBytes, getDownloadURL, type FirebaseStorage } from "firebase/storage";

/**
 * Build the Firebase Storage path for a claim's proof photo.
 *
 * Pure function — no I/O — so it's trivially testable.
 *
 * @param householdId The household the claim belongs to (used by storage rules)
 * @param claimId The claim document id
 * @param timestamp Optional timestamp (ms since epoch) to use in the file name.
 *   Defaults to `Date.now()`. Exposed for deterministic tests.
 */
export function buildPhotoStoragePath(
  householdId: string,
  claimId: string,
  timestamp: number = Date.now(),
): string {
  return `responsibilities/${householdId}/${claimId}/${timestamp}.jpg`;
}

/**
 * Compress + upload a proof photo and return its download URL.
 *
 * Reuses the existing `compressForContent` helper (1200px max, 80% JPEG)
 * exactly like `ChatDetail.uploadChatImage` does — keeps payload sizes
 * consistent across image uploads.
 *
 * Errors propagate so the caller can surface a toast / keep the user's
 * status update from being applied if upload was the user's intent.
 *
 * @param file Raw File from a `<input type="file" accept="image/*">`
 * @param storage The Firebase Storage instance (injected so tests can mock)
 * @param householdId Household id for the storage path
 * @param claimId The responsibility claim id
 * @param timestamp Optional override for testing
 */
export async function uploadResponsibilityClaimPhoto(
  file: File,
  storage: FirebaseStorage,
  householdId: string,
  claimId: string,
  timestamp: number = Date.now(),
): Promise<string> {
  // Lazy-import to keep the bundler from pulling canvas code into pages
  // that never trigger an upload (mirrors ChatDetail's pattern).
  const { compressForContent } = await import("@/lib/imageCompressor");
  const compressed = await compressForContent(file);
  const path = buildPhotoStoragePath(householdId, claimId, timestamp);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, compressed);
  return await getDownloadURL(storageRef);
}
